import { graphql } from 'react-apollo';

import type {
  ShortFormContent_CourseLessons as CourseLessons,
  ShortFormContent_CourseVideos as CourseVideos,
} from '__generated__/graphql-types';

import {
  ClipsCourseDetailsQuery,
  SFCVLPCourseMaterialsQuery,
} from 'bundles/clips/components/short-form-content/SFCVLPQueries';
import type { OnDemandVideos, ProgramSuggestion } from 'bundles/clips/components/short-form-content/SFCVLPSharedTypes';
import ClipsVideoData from 'bundles/clips/components/short-form-content/ShortFormContentVideoData.graphql';
import type {
  ClipsCourseDetailsQuery as ClipsCourseDetailsQueryData,
  ClipsCourseDetailsQueryVariables,
  ClipsCourseDetailsQuery_OnDemandCoursesV1Resource_slug_elements as DetailsOnDemandCourse,
  ClipsCourseDetailsQuery_XdpV1Resource_slug_elements as SFCVLPCourseXDPData,
} from 'bundles/clips/components/short-form-content/__generated__/ClipsCourseDetailsQuery';
import type {
  SFCVLPCourseMaterialsQuery_OnDemandLearnerMaterialsV1Resource_courses_elements_learnerMaterialItems_lectures as Item,
  SFCVLPCourseMaterialsQuery as SFCVLPCourseMaterialsQueryData,
  SFCVLPCourseMaterialsQueryVariables,
} from 'bundles/clips/components/short-form-content/__generated__/SFCVLPCourseMaterialsQuery';
import type {
  ClipsVideoDataQuery as SFCVideoDataQuery,
  ClipsVideoDataQueryVariables as SFCVideoDataQueryVariables,
} from 'bundles/clips/components/short-form-content/__generated__/ShortFormContentVideoData';
import {
  extractNonVideoCourseItems,
  extractSFCData,
  filterExistsOrDefaultCopied,
} from 'bundles/clips/components/short-form-content/utils';
import type { Props, PropsFromRouter } from 'bundles/clips/components/short-form-content/withSFCVLPData';
import type { VLPItem, VLPLesson, VLPModule } from 'bundles/clips/types/vlpSharedTypes';
import {
  extractVideoLectureItems,
  getCurrentLessonFromItem,
  getCurrentModuleFromLesson,
  getNextVideoItem,
  getNonVideoCourseItems,
  getVideoItem,
  updateItems,
  updateLessonsTitles,
} from 'bundles/clips/utils/utils';
import { thirdPartyOrganizationsQuery } from 'bundles/enterprise-legacy-learner-home/queries/ThirdPartyOrganizationsQuery';
import type {
  ThirdPartyOrganizationsQuery,
  ThirdPartyOrganizationsQueryVariables,
} from 'bundles/enterprise-legacy-learner-home/queries/__generated__/ThirdPartyOrganizationsQuery';
import { InternalServerError, NotFoundError } from 'bundles/ssr/lib/errors/directives';
import inServerContext from 'bundles/ssr/util/inServerContext';

export type PropsFromClipsCourseDetailsQuery = {
  xdpApiData: SFCVLPCourseXDPData;
  onDemandCourse: DetailsOnDemandCourse;
  course?: {
    id: string;
    slug: string;
    name: string;
    description: string;
    partnerIds?: Array<string>;
  };
  courseId?: string;
  courseDetailsQueryDone: boolean;
  csrError: boolean;
};

export type PropsFromSFCVLPCourseMaterialsQuery = {
  currentModule?: VLPModule;
  currentLesson?: VLPLesson;
  currentVideoItem?: VLPItem;
  nextVideoItem: VLPItem;
  lectureItems: Array<VLPItem>;
  courseMaterialsQueryDone: boolean;
  csrError: boolean;
  nonVideoCourseItemsForLesson?: Item[];
  lessons: Array<VLPLesson>;
};

export type ShortFormContentVideoAssets = {
  likes?: number;
  views?: number;
  preferredAvailableForEnrollmentProgram?: ProgramSuggestion | null;
  suggestedToJoinProgram?: ProgramSuggestion | null;
  accessProvidedViaProgram?: ProgramSuggestion | null;
  userEnrolledIntoCourse: boolean;
  onDemandVideos?: OnDemandVideos;
  videoDataDone: boolean;
  justEnrolledThroughMutation?: boolean;
  hasSFCVideoDataError?: boolean;
};

export type PropsFromWithOrganizationId = {
  orgId?: string | null;
};

type PropsFromSFCVideoData = {
  userEnrolledIntoCourse: boolean;
  courseVideos: CourseVideos;
  courseLessons: CourseLessons;
};

export const withSFCVLPCourseDetailsData = graphql<
  {},
  ClipsCourseDetailsQueryData,
  ClipsCourseDetailsQueryVariables,
  PropsFromClipsCourseDetailsQuery
>(ClipsCourseDetailsQuery, {
  props: ({ data }) => {
    const xdpApiData = filterExistsOrDefaultCopied(data?.XdpV1Resource?.slug?.elements)[0];
    const onDemandCourse = filterExistsOrDefaultCopied(data?.OnDemandCoursesV1Resource?.slug?.elements)[0];
    let queryCsrError = false;

    if (!data?.loading && !xdpApiData) {
      // @ts-expect-error Property 'statusCode' does not exist on type 'Error'
      if (data?.error?.networkError?.statusCode === 500) {
        // @ts-expect-error ts-migrate(2554) FIXME: Expected 1 arguments, but got 0.
        throw new InternalServerError();
      } else if (inServerContext) {
        throw new NotFoundError();
      } else {
        queryCsrError = true;
      }
    }

    let course;

    if (
      xdpApiData?.xdpMetadata &&
      xdpApiData?.xdpMetadata?.__typename === 'XdpV1_cdpMetadataMember' &&
      xdpApiData?.xdpMetadata?.cdpMetadata
    ) {
      const { cdpMetadata } = xdpApiData.xdpMetadata;
      course = {
        id: cdpMetadata.id,
        slug: cdpMetadata.slug,
        name: cdpMetadata.name,
        description: cdpMetadata.description,
        partnerIds: cdpMetadata.partners.map((partner: $TSFixMe) => partner.id),
        isStandaloneCourse: cdpMetadata?.courseTypeMetadata?.__typename === 'XdpV1_shortFormContentMember',
      };
    }

    return {
      xdpApiData,
      onDemandCourse,
      course,
      courseId: course?.id,
      courseDetailsQueryDone: !data?.loading,
      csrError: queryCsrError,
    };
  },
});

export const withSFCVLPCourseMaterialsData = graphql<
  PropsFromRouter & PropsFromClipsCourseDetailsQuery & PropsFromSFCVideoData,
  SFCVLPCourseMaterialsQueryData,
  SFCVLPCourseMaterialsQueryVariables,
  PropsFromSFCVLPCourseMaterialsQuery
>(SFCVLPCourseMaterialsQuery, {
  skip: ({ courseId }) => !courseId,
  options: ({ courseId, userEnrolledIntoCourse }) => {
    return {
      variables: {
        // see skip above
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        courseId: courseId!,
        anonymousAccess: !userEnrolledIntoCourse,
      },
    };
  },
  props: ({ data, ownProps: { itemId, courseId, csrError, courseVideos, courseLessons } }) => {
    const onDemandLearnerMaterials = filterExistsOrDefaultCopied(
      data?.OnDemandLearnerMaterialsV1Resource?.courses?.elements
    )[0];
    const modules = filterExistsOrDefaultCopied(onDemandLearnerMaterials?.learnerMaterialModules?.elements);
    const items = updateItems(
      filterExistsOrDefaultCopied(onDemandLearnerMaterials?.learnerMaterialItems?.lectures),
      courseVideos?.videos
    );
    const lessons = updateLessonsTitles(
      filterExistsOrDefaultCopied(onDemandLearnerMaterials?.learnerMaterialLessons?.elements),
      courseLessons?.lessons
    );
    const currentLesson = getCurrentLessonFromItem(lessons, itemId);
    const lessonId = currentLesson?.id;
    const currentModule = getCurrentModuleFromLesson(modules, lessonId);
    const lectureItems = extractVideoLectureItems(items);
    const nonVideoCourseItems = items && extractNonVideoCourseItems(items);
    const currentVideoItem = items && getVideoItem(items, itemId);
    const nextVideoItem = lectureItems && getNextVideoItem(lectureItems, itemId);
    // since this gets skipped, if we have the courseId (the data this query needs before running) and loading is false, this completed
    const nonVideoCourseItemsForLesson = getNonVideoCourseItems(nonVideoCourseItems, lessonId);
    let queryDone = false;
    let queryCsrError = false;
    const hasFetchedData = lectureItems && currentVideoItem && nextVideoItem;
    if (!data?.loading && courseId) {
      queryDone = true;
      // TODO: Fix ts error: Property 'statusCode' does not exist on type 'Error'.
      // @ts-expect-error
      if (data?.error?.networkError?.statusCode === 500) {
        // @ts-expect-error ts-migrate(2554) FIXME: Expected 1 arguments, but got 0.
        throw new InternalServerError();
      } else if (!hasFetchedData) {
        if (inServerContext) {
          throw new NotFoundError();
        } else {
          queryCsrError = true;
        }
      }
    }

    return {
      currentModule,
      currentLesson,
      currentVideoItem,
      nextVideoItem,
      nonVideoCourseItems,
      lectureItems,
      lessons,
      courseMaterialsQueryDone: queryDone,
      nonVideoCourseItemsForLesson,
      csrError: csrError || queryCsrError,
    };
  },
});

export const withSFCVideoData = graphql<
  Props,
  SFCVideoDataQuery,
  SFCVideoDataQueryVariables,
  ShortFormContentVideoAssets
>(ClipsVideoData, {
  skip: ({ csrError, courseId }) => csrError || !courseId,
  // TODO: Fix ssr refresh issue causing page to load twice
  options: ({ programSlug, courseId, itemId }) => ({
    context: { clientName: 'gatewayGql' },
    fetchPolicy: 'cache-and-network',
    ssr: false,
    variables: {
      programSlug,
      // see skip above
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      courseId: courseId!,
      itemId,
    },
  }),
  props: ({ data, ownProps: { courseDetailsQueryDone } }) => {
    const videoData = data?.ShortFormContent?.queryVideoData;
    return extractSFCData(videoData, !courseDetailsQueryDone || !!data?.loading, data?.error);
  },
});

export const withOrganizationId = graphql<
  PropsFromRouter,
  ThirdPartyOrganizationsQuery,
  ThirdPartyOrganizationsQueryVariables,
  PropsFromWithOrganizationId
>(thirdPartyOrganizationsQuery, {
  skip: ({ authProvider }) => !authProvider,
  // see skip above
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  options: ({ authProvider }) => ({ variables: { orgSlug: authProvider! } }),
  props: ({ data }) => {
    const orgId = data?.ThirdPartyOrganizationsV1Resource?.bySlug?.elements?.[0]?.id;
    return { orgId };
  },
});
